import {
  Button,
  createStyles,
  Grid,
  Paper,
  TextField,
  Typography,
  withStyles,
  WithStyles,
} from "@material-ui/core";
import { Trans, useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import React from "react";
import type {
  AgeRange,
  EnrichedAnimalCategory,
  WeightGainCurve,
} from "shared/types";
import type {
  CategoryFieldsVisibility,
  CategoryFormState,
} from "../category.types";
import { useCategoryForm } from "../hooks/useCategoryForm";
import { useCategoryFormOptions } from "../hooks/useCategoryFormOptions";
import { WARNING_ICON_PATH } from "../utils/category.utils";

type CategoryFormProps = {
  ageRanges: AgeRange[];
  categories: EnrichedAnimalCategory[];
  fieldsVisibility: CategoryFieldsVisibility;
  onCancel: () => void;
  onSubmit: (formState: CategoryFormState) => void;
  selectedCategory: EnrichedAnimalCategory | null;
  weightGainCurves: WeightGainCurve[];
} & WithStyles<typeof styles>;

function CategoryForm({
  ageRanges,
  categories,
  classes,
  fieldsVisibility,
  onCancel,
  onSubmit,
  selectedCategory,
  weightGainCurves,
}: CategoryFormProps) {
  const { t } = useTranslations();

  const {
    canSubmitForm,
    formState,
    handleChangeAgeRange,
    handleChangeGender,
    handleChangeHeadCount,
    handleChangeInitialAmountForConciliation,
    handleChangeName,
    handleChangeWeightGainCurve,
    handleResetForm,
    handleSubmitForm,
  } = useCategoryForm({
    categories,
    onSubmit,
    selectedCategory,
  });

  const { ageRangeOptions, genderOptions, weightGainCurveOptions } =
    useCategoryFormOptions({
      ageRanges,
      selectedGender: formState.gender.value,
      weightGainCurves,
    });

  const handleCancelClick = () => {
    handleResetForm();
    onCancel();
  };

  const isEditing = selectedCategory != null;
  const headerText = isEditing
    ? t("animalCategory:edit-category")
    : t("animalCategory:new-category");

  return (
    <Paper className={classes.paper} square={true}>
      <div className={classes.root}>
        <Grid item sm={12} xs={12}>
          <Typography className={classes.formHeader} gutterBottom={true}>
            {headerText}
          </Typography>
        </Grid>
        <FormDisclaimer classes={classes} />
        <Grid item sm={12} xs={12}>
          <Typography className={classes.subheader} gutterBottom>
            {t("animalCategory:enter-category-information")}
          </Typography>
        </Grid>
        <div className={classes.fieldsWrapper}>
          <TextField
            autoFocus={true}
            data-testid="category-name-input"
            error={formState.name.error != null}
            helperText={formState.name.error}
            InputLabelProps={{ className: classes.inputLabel }}
            label={t("common:fields.name")}
            onChange={handleChangeName}
            placeholder={t("common:fields.enter-a-name")}
            required={true}
            value={formState.name.value}
          />
          {fieldsVisibility.showHeadCount && (
            <TextField
              data-testid="head-count-input"
              error={formState.headCount.error != null}
              InputLabelProps={{ className: classes.inputLabel }}
              label={t("animalCategory:desired-size")}
              onChange={handleChangeHeadCount}
              placeholder={t("animalCategory:enter-desired-size")}
              value={formState.headCount.value ?? ""}
            />
          )}
          {fieldsVisibility.showAmountForConciliation && (
            <TextField
              data-testid="conciliation-amount-input"
              error={formState.initialAmountForConciliation.error != null}
              InputLabelProps={{ className: classes.inputLabel }}
              label={t("common:initial-amount")}
              onChange={handleChangeInitialAmountForConciliation}
              placeholder={t("animalCategory:enter-desired-amount")}
              value={formState.initialAmountForConciliation.value ?? ""}
            />
          )}
          <SelectDropdown
            className={classes.genderInput}
            data-testid="gender-select"
            error={formState.gender.error != null}
            helperText={formState.gender.error}
            label={{ text: t("common:gender"), required: true }}
            onChange={handleChangeGender}
            options={genderOptions}
            value={formState.gender.value}
          />
          <SelectDropdown
            className={classes.ageRangeInput}
            data-testid="age-range-select"
            error={formState.ageRangeId.error != null}
            helperText={formState.ageRangeId.error}
            label={{ text: t("animalCategory:age-range"), required: true }}
            onChange={handleChangeAgeRange}
            options={ageRangeOptions}
            value={formState.ageRangeId.value}
          />
          {fieldsVisibility.showWeightGain && (
            <SelectDropdown
              className={classes.weightGainCurveInput}
              data-testid="weight-gain-curve-select"
              isClearable={true}
              isDisabled={!weightGainCurves.length}
              label={{ text: t("livestock:weight-gain-curve") }}
              onChange={handleChangeWeightGainCurve}
              options={weightGainCurveOptions}
              value={formState.weightGainCurveId.value}
            />
          )}
        </div>
        <Grid item sm={12} xs={12} className={classes.formActionsContainer}>
          <FormActions
            canSubmit={canSubmitForm}
            isEditing={isEditing}
            onCancel={handleCancelClick}
            onSubmit={handleSubmitForm}
          />
        </Grid>
      </div>
    </Paper>
  );
}

type FormDisclaimerProps = WithStyles<typeof styles>;

function FormDisclaimer({ classes }: FormDisclaimerProps) {
  const { t } = useTranslations();

  return (
    <Typography
      className={classes.formDisclaimer}
      data-testid="form-disclaimer"
    >
      <img
        alt={t("common:warning-icon")}
        src={WARNING_ICON_PATH}
        className={classes.disclaimerIcon}
      />
      <Trans
        i18nKey="animalCategory:create-new-category-disclaimer"
        components={{ bold: <strong /> }}
      />
    </Typography>
  );
}

type FormActionsProps = {
  canSubmit: boolean;
  isEditing: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

function FormActions({
  canSubmit,
  isEditing,
  onCancel,
  onSubmit,
}: FormActionsProps) {
  const { t } = useTranslations();

  const submitButtonText = isEditing
    ? t("common:actions.save")
    : t("common:actions.add");

  return (
    <>
      <Button color="primary" data-testid="cancel-button" onClick={onCancel}>
        {t("common:actions.cancel")}
      </Button>
      <Button
        color="primary"
        data-testid="submit-button"
        disabled={!canSubmit}
        onClick={onSubmit}
        variant="contained"
      >
        {submitButtonText}
      </Button>
    </>
  );
}

const styles = () =>
  createStyles({
    paper: {
      borderRadius: "16px",
      marginTop: "10px",
      padding: "20px",
      width: "100%",
    },
    root: {
      paddingBottom: "16px",
    },
    formHeader: {
      color: "#333333",
      fontSize: "20px",
      fontStretch: "medium",
      fontStyle: "medium",
      fontWeight: "bold",
      letterSpacing: "medium",
      lineHeight: "medium",
      marginTop: "16px",
    },
    formDisclaimer: {
      margin: "26px 0px 26px 0px",
      fontFamily: "Roboto",
      lineHeight: 1.71,
      color: "#666",
    },
    disclaimerIcon: {
      marginRight: "4px",
      marginBottom: "-3px",
      height: "20px",
      color: "red",
    },
    subheader: {
      width: "227px",
      height: "16px",
      fontFamily: "Roboto",
      fontSize: "14px",
      fontWeight: 500,
      fontStyle: "normal",
      fontStretch: "normal",
      lineHeight: "normal",
      letterSpacing: "-0.03px",
      color: "#535353",
      marginTop: "24px",
      marginBottom: "16px",
    },
    fieldsWrapper: {
      display: "flex",
      flexDirection: "row",
      gap: "32px",
      flexWrap: "wrap",
    },
    inputLabel: {
      width: "max-content",
    },
    genderInput: {
      width: "100px !important",
    },
    ageRangeInput: {
      width: "250px !important",
    },
    weightGainCurveInput: {
      width: "250px !important",
    },
    formActionsContainer: {
      marginTop: "36px",
      display: "flex",
      flexDirection: "row",
      gap: "20px",
      justifyContent: "flex-end",
      width: "100%",
    },
  });

export default withStyles(styles)(CategoryForm);
